import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useAuth from "../../hooks/useAuth";

/**
 * HelpContent - User guide and documentation for Online-CADCOM
 * Provides step-by-step instructions for using the platform
 */
const HelpContent = () => {
    const { t } = useTranslation();
    const { auth } = useAuth();
    const isAuthenticated = !!auth?.accessToken;
    const userRole = auth?.role?.toLowerCase();
    const [activeSection, setActiveSection] = useState('getting-started');

    const sections = [
        { id: 'getting-started', icon: 'bi-rocket-takeoff', title: t('helpGettingStarted') },
        { id: 'for-users', icon: 'bi-person', title: t('helpForUsers') },
        { id: 'tool-selection', icon: 'bi-sliders', title: t('helpToolSelection') },
        { id: 'ai-assistant', icon: 'bi-robot', title: t('helpAIAssistant') },
        ...(userRole === 'admin' ? [{ id: 'for-admins', icon: 'bi-shield-check', title: t('helpForAdmins') }] : []),
        { id: 'faq', icon: 'bi-question-circle', title: t('helpFAQ') },
    ];

    return (
        <div className="container px-3">
            {/* Hero Section */}
            <div className="row mb-4">
                <div className="col-12 text-center">
                    <div className="py-4">
                        <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                            style={{ width: '80px', height: '80px', backgroundColor: '#e8f4fd' }}>
                            <i className="bi bi-book" style={{ fontSize: '2.5rem', color: '#112d4e' }}></i>
                        </div>
                        <h1 style={{ color: '#112d4e', fontWeight: 700, marginBottom: '16px' }}>
                            {t("helpTitle")}
                        </h1>
                        <p className="lead text-muted" style={{ maxWidth: '600px', margin: '0 auto' }}>
                            {t("helpSubtitle")}
                        </p>
                    </div>
                </div>
            </div>

            <div className="row">
                {/* Navigation Sidebar */}
                <div className="col-lg-3 mb-4">
                    <div className="card border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
                        <div className="card-body p-0">
                            <nav className="nav flex-column">
                                {sections.map((section) => (
                                    <button
                                        key={section.id}
                                        className={`nav-link text-start border-0 bg-transparent py-3 px-4 ${activeSection === section.id ? 'active fw-bold' : ''}`}
                                        style={{
                                            color: activeSection === section.id ? '#112d4e' : '#6c757d',
                                            borderLeft: activeSection === section.id ? '3px solid #112d4e' : '3px solid transparent',
                                            backgroundColor: activeSection === section.id ? '#f8f9fa' : 'transparent'
                                        }}
                                        onClick={() => setActiveSection(section.id)}
                                    >
                                        <i className={`${section.icon} me-2`}></i>
                                        {section.title}
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="col-lg-9">
                    {/* Getting Started */}
                    {activeSection === 'getting-started' && (
                        <div className="card border-0 shadow-sm mb-4">
                            <div className="card-body p-4 p-lg-5">
                                <h2 style={{ color: '#112d4e', fontWeight: 600, marginBottom: '24px' }}>
                                    <i className="bi bi-rocket-takeoff me-2"></i>
                                    {t('helpGettingStarted')}
                                </h2>

                                <div className="mb-4">
                                    <h5 style={{ color: '#112d4e' }}>{t('helpWhatIsPlatform')}</h5>
                                    <p className="text-secondary">{t('helpWhatIsPlatformDesc')}</p>
                                </div>

                                <div className="mb-4">
                                    <h5 style={{ color: '#112d4e' }}>{t('helpHowToRegister')}</h5>
                                    <ol className="text-secondary">
                                        <li className="mb-2">{t('helpRegisterStep1')}</li>
                                        <li className="mb-2">{t('helpRegisterStep2')}</li>
                                        <li className="mb-2">{t('helpRegisterStep3')}</li>
                                        <li className="mb-2">{t('helpRegisterStep4')}</li>
                                    </ol>
                                </div>

                                {!isAuthenticated && (
                                    <div className="d-flex gap-3 mt-4">
                                        <Link to="/register" className="btn btn-primary" style={{ backgroundColor: '#112d4e', border: 'none' }}>
                                            <i className="bi bi-person-plus me-2"></i>
                                            {t('helpRegisterNow')}
                                        </Link>
                                        <Link to="/login" className="btn btn-outline-secondary">
                                            <i className="bi bi-box-arrow-in-right me-2"></i>
                                            {t('helpLoginNow')}
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* For Users */}
                    {activeSection === 'for-users' && (
                        <div className="card border-0 shadow-sm mb-4">
                            <div className="card-body p-4 p-lg-5">
                                <h2 style={{ color: '#112d4e', fontWeight: 600, marginBottom: '24px' }}>
                                    <i className="bi bi-person me-2"></i>
                                    {t('helpForUsers')}
                                </h2>

                                <div className="accordion" id="userAccordion">
                                    {/* Submit Tool Passport */}
                                    <div className="accordion-item border-0 mb-3">
                                        <h2 className="accordion-header">
                                            <button className="accordion-button collapsed bg-light" type="button" data-bs-toggle="collapse" data-bs-target="#submitTool">
                                                <i className="bi bi-plus-circle me-2 text-success"></i>
                                                {t('helpSubmitToolTitle')}
                                            </button>
                                        </h2>
                                        <div id="submitTool" className="accordion-collapse collapse" data-bs-parent="#userAccordion">
                                            <div className="accordion-body">
                                                <p>{t('helpSubmitToolDesc')}</p>
                                                <ol className="text-secondary">
                                                    <li className="mb-2">{t('helpSubmitToolStep1')}</li>
                                                    <li className="mb-2">{t('helpSubmitToolStep2')}</li>
                                                    <li className="mb-2">{t('helpSubmitToolStep3')}</li>
                                                    <li className="mb-2">{t('helpSubmitToolStep4')}</li>
                                                </ol>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Submit Criteria */}
                                    <div className="accordion-item border-0 mb-3">
                                        <h2 className="accordion-header">
                                            <button className="accordion-button collapsed bg-light" type="button" data-bs-toggle="collapse" data-bs-target="#submitCriteria">
                                                <i className="bi bi-list-check me-2 text-primary"></i>
                                                {t('helpSubmitCriteriaTitle')}
                                            </button>
                                        </h2>
                                        <div id="submitCriteria" className="accordion-collapse collapse" data-bs-parent="#userAccordion">
                                            <div className="accordion-body">
                                                <p>{t('helpSubmitCriteriaDesc')}</p>
                                                <ol className="text-secondary">
                                                    <li className="mb-2">{t('helpSubmitCriteriaStep1')}</li>
                                                    <li className="mb-2">{t('helpSubmitCriteriaStep2')}</li>
                                                    <li className="mb-2">{t('helpSubmitCriteriaStep3')}</li>
                                                </ol>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Track Submissions */}
                                    <div className="accordion-item border-0 mb-3">
                                        <h2 className="accordion-header">
                                            <button className="accordion-button collapsed bg-light" type="button" data-bs-toggle="collapse" data-bs-target="#trackSubmissions">
                                                <i className="bi bi-eye me-2 text-info"></i>
                                                {t('helpTrackSubmissionsTitle')}
                                            </button>
                                        </h2>
                                        <div id="trackSubmissions" className="accordion-collapse collapse" data-bs-parent="#userAccordion">
                                            <div className="accordion-body">
                                                <p>{t('helpTrackSubmissionsDesc')}</p>
                                                <ul className="text-secondary">
                                                    <li className="mb-2"><strong>{t('helpStatusPending')}:</strong> {t('helpStatusPendingDesc')}</li>
                                                    <li className="mb-2"><strong>{t('helpStatusApproved')}:</strong> {t('helpStatusApprovedDesc')}</li>
                                                    <li className="mb-2"><strong>{t('helpStatusRejected')}:</strong> {t('helpStatusRejectedDesc')}</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tool Selection */}
                    {activeSection === 'tool-selection' && (
                        <div className="card border-0 shadow-sm mb-4">
                            <div className="card-body p-4 p-lg-5">
                                <h2 style={{ color: '#112d4e', fontWeight: 600, marginBottom: '24px' }}>
                                    <i className="bi bi-sliders me-2"></i>
                                    {t('helpToolSelection')}
                                </h2>

                                <p className="text-secondary mb-4">{t('helpToolSelectionDesc')}</p>

                                <div className="row g-4 mb-4">
                                    <div className="col-md-6">
                                        <div className="p-4 rounded-3 h-100" style={{ backgroundColor: '#f8f9fa' }}>
                                            <h5 style={{ color: '#112d4e' }}>
                                                <i className="bi bi-1-circle me-2"></i>
                                                {t('helpSelectCategoryTitle')}
                                            </h5>
                                            <p className="text-secondary mb-0">{t('helpSelectCategoryDesc')}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="p-4 rounded-3 h-100" style={{ backgroundColor: '#f8f9fa' }}>
                                            <h5 style={{ color: '#112d4e' }}>
                                                <i className="bi bi-2-circle me-2"></i>
                                                {t('helpSetCriteriaTitle')}
                                            </h5>
                                            <p className="text-secondary mb-0">{t('helpSetCriteriaDesc')}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="p-4 rounded-3 h-100" style={{ backgroundColor: '#f8f9fa' }}>
                                            <h5 style={{ color: '#112d4e' }}>
                                                <i className="bi bi-3-circle me-2"></i>
                                                {t('helpChooseMethodTitle')}
                                            </h5>
                                            <p className="text-secondary mb-0">{t('helpChooseMethodDesc')}</p>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="p-4 rounded-3 h-100" style={{ backgroundColor: '#f8f9fa' }}>
                                            <h5 style={{ color: '#112d4e' }}>
                                                <i className="bi bi-4-circle me-2"></i>
                                                {t('helpViewResultsTitle')}
                                            </h5>
                                            <p className="text-secondary mb-0">{t('helpViewResultsDesc')}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="alert alert-info mb-0">
                                    <i className="bi bi-info-circle me-2"></i>
                                    <strong>{t('helpMCDMMethods')}:</strong> MAUT, PROMETHEE, TOPSIS, VIKOR, COPRAS
                                </div>
                            </div>
                        </div>
                    )}

                    {/* AI Assistant */}
                    {activeSection === 'ai-assistant' && (
                        <div className="card border-0 shadow-sm mb-4">
                            <div className="card-body p-4 p-lg-5">
                                <h2 style={{ color: '#112d4e', fontWeight: 600, marginBottom: '24px' }}>
                                    <i className="bi bi-robot me-2"></i>
                                    {t('helpAIAssistant')}
                                </h2>

                                <p className="text-secondary mb-4">{t('helpAIAssistantDesc')}</p>

                                <div className="p-4 rounded-3 mb-4" style={{ backgroundColor: '#e8f5e9' }}>
                                    <h5 style={{ color: '#2e7d32' }}>
                                        <i className="bi bi-lightbulb me-2"></i>
                                        {t('helpAIHowToUseTitle')}
                                    </h5>
                                    <ol className="text-secondary mb-0">
                                        <li className="mb-2">{t('helpAIStep1')}</li>
                                        <li className="mb-2">{t('helpAIStep2')}</li>
                                        <li className="mb-2">{t('helpAIStep3')}</li>
                                    </ol>
                                </div>

                                <div className="alert alert-warning mb-0">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    {t('helpAIRateLimitNote')}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* For Admins */}
                    {activeSection === 'for-admins' && userRole === 'admin' && (
                        <div className="card border-0 shadow-sm mb-4">
                            <div className="card-body p-4 p-lg-5">
                                <h2 style={{ color: '#112d4e', fontWeight: 600, marginBottom: '24px' }}>
                                    <i className="bi bi-shield-check me-2"></i>
                                    {t('helpForAdmins')}
                                </h2>

                                <div className="row g-4">
                                    <div className="col-md-6">
                                        <div className="card h-100 border">
                                            <div className="card-body">
                                                <h5 style={{ color: '#112d4e' }}>
                                                    <i className="bi bi-people me-2"></i>
                                                    {t('helpAdminUsersTitle')}
                                                </h5>
                                                <p className="text-secondary">{t('helpAdminUsersDesc')}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="card h-100 border">
                                            <div className="card-body">
                                                <h5 style={{ color: '#112d4e' }}>
                                                    <i className="bi bi-check2-square me-2"></i>
                                                    {t('helpAdminApprovalsTitle')}
                                                </h5>
                                                <p className="text-secondary">{t('helpAdminApprovalsDesc')}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="card h-100 border">
                                            <div className="card-body">
                                                <h5 style={{ color: '#112d4e' }}>
                                                    <i className="bi bi-database me-2"></i>
                                                    {t('helpAdminDataTitle')}
                                                </h5>
                                                <p className="text-secondary">{t('helpAdminDataDesc')}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="card h-100 border">
                                            <div className="card-body">
                                                <h5 style={{ color: '#112d4e' }}>
                                                    <i className="bi bi-graph-up me-2"></i>
                                                    {t('helpAdminReportsTitle')}
                                                </h5>
                                                <p className="text-secondary">{t('helpAdminReportsDesc')}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* FAQ */}
                    {activeSection === 'faq' && (
                        <div className="card border-0 shadow-sm mb-4">
                            <div className="card-body p-4 p-lg-5">
                                <h2 style={{ color: '#112d4e', fontWeight: 600, marginBottom: '24px' }}>
                                    <i className="bi bi-question-circle me-2"></i>
                                    {t('helpFAQ')}
                                </h2>

                                <div className="accordion" id="faqAccordion">
                                    <div className="accordion-item border-0 mb-3">
                                        <h2 className="accordion-header">
                                            <button className="accordion-button collapsed bg-light" type="button" data-bs-toggle="collapse" data-bs-target="#faq1">
                                                {t('helpFAQ1Q')}
                                            </button>
                                        </h2>
                                        <div id="faq1" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                            <div className="accordion-body text-secondary">{t('helpFAQ1A')}</div>
                                        </div>
                                    </div>

                                    <div className="accordion-item border-0 mb-3">
                                        <h2 className="accordion-header">
                                            <button className="accordion-button collapsed bg-light" type="button" data-bs-toggle="collapse" data-bs-target="#faq2">
                                                {t('helpFAQ2Q')}
                                            </button>
                                        </h2>
                                        <div id="faq2" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                            <div className="accordion-body text-secondary">{t('helpFAQ2A')}</div>
                                        </div>
                                    </div>

                                    <div className="accordion-item border-0 mb-3">
                                        <h2 className="accordion-header">
                                            <button className="accordion-button collapsed bg-light" type="button" data-bs-toggle="collapse" data-bs-target="#faq3">
                                                {t('helpFAQ3Q')}
                                            </button>
                                        </h2>
                                        <div id="faq3" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                            <div className="accordion-body text-secondary">{t('helpFAQ3A')}</div>
                                        </div>
                                    </div>

                                    <div className="accordion-item border-0 mb-3">
                                        <h2 className="accordion-header">
                                            <button className="accordion-button collapsed bg-light" type="button" data-bs-toggle="collapse" data-bs-target="#faq4">
                                                {t('helpFAQ4Q')}
                                            </button>
                                        </h2>
                                        <div id="faq4" className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                            <div className="accordion-body text-secondary">{t('helpFAQ4A')}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Contact Support */}
                    <div className="card border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="card-body p-4 text-center">
                            <h5 style={{ color: '#112d4e' }}>{t('helpStillNeedHelp')}</h5>
                            <p className="text-muted mb-3">{t('helpContactSupport')}</p>
                            <a href="mailto:lavdim.menxhiqi@ubt-uni.net" className="btn btn-outline-primary">
                                <i className="bi bi-envelope me-2"></i>
                                {t('helpEmailSupport')}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HelpContent;
